import { cn } from "@/lib/utils";

/**
 * Color-coded P% chip mirroring the addon UI thresholds:
 *   < 12%  → red (low)
 *   12-25% → yellow (medium)
 *   ≥ 25%  → green (high)
 */

export function probabilityTier(p: number): "low" | "medium" | "high" {
  if (p >= 0.25) return "high";
  if (p >= 0.12) return "medium";
  return "low";
}

interface Props {
  p: number;
  hits?: number;
  poolSize?: number;
  className?: string;
  size?: "sm" | "md";
}

export function ProbabilityPill({ p, hits, poolSize, className, size = "md" }: Props) {
  const tier = probabilityTier(p);
  const color =
    tier === "high" ? "text-success border-success/40 bg-success/10"
    : tier === "medium" ? "text-warning border-warning/40 bg-warning/10"
    : "text-danger border-danger/40 bg-danger/10";
  const padding = size === "sm" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-xs border font-mono tabular whitespace-nowrap",
        padding,
        color,
        className,
      )}
    >
      <span className="font-semibold">{(p * 100).toFixed(0)}%</span>
      {typeof hits === "number" && typeof poolSize === "number" && (
        <span className="opacity-70">
          {hits}/{poolSize}
        </span>
      )}
    </span>
  );
}

/** CSS color value for a probability tier (used for Melli bar fills). */
export function probabilityColorVar(p: number): string {
  const tier = probabilityTier(p);
  if (tier === "high") return "var(--color-success)";
  if (tier === "medium") return "var(--color-warning)";
  return "var(--color-danger)";
}
